/**
 * adminAccess.js — shared predicate for "can this signed-in user reach the
 * admin shell (/admin)?"
 *
 * `projectRoles` come from fetchUserProjectRoles.php, which serializes each
 * assignment as `${projectid}_${PascalCaseRole}` — e.g. "CityOfMadison_Admin"
 * or "CityOfMadison_Mod" (see toPascalCase() there). Because the role suffix
 * is PascalCase, the comparison here MUST be case-insensitive so it matches
 * the server's PROJECT_ADMIN_ROLES (['admin','mod']) in firebase_auth.php.
 *
 * Regression context (BUG-C, 2026-07-01): AdminDashboard previously compared
 * the suffix against lowercase 'admin'/'mod' only. Since the real suffix is
 * "Admin"/"Mod", every project-scoped admin/mod (e.g. the Madison librarian)
 * saw the "Admin Console" button — MapComponent.adminOrMod() checks
 * `${projectid}_Admin`/`_Mod`, which DID match — but got bounced straight back
 * to the map by this gate. Keep client and server role checks in sync here.
 */

export const PROJECT_ADMIN_ROLE_SUFFIXES = ['admin', 'mod'];

/**
 * Extract the trailing role token from a serialized `${projectid}_${Role}`
 * string. Splits on the LAST underscore so projectids that themselves contain
 * underscores (e.g. "American_RIAD_Admin") still resolve to "Admin".
 */
export function extractRoleSuffix(role) {
  if (typeof role !== 'string') return '';
  const idx = role.lastIndexOf('_');
  return idx > 0 ? role.substring(idx + 1) : '';
}

/**
 * True when the caller has admin standing somewhere: a global admin, or an
 * admin/mod on at least one project. Case-insensitive on the role suffix.
 */
export function hasAnyAdminAccess(isGlobalAdmin, projectRoles) {
  if (isGlobalAdmin) return true;
  if (!Array.isArray(projectRoles) || projectRoles.length === 0) return false;
  return projectRoles.some((r) =>
    PROJECT_ADMIN_ROLE_SUFFIXES.includes(extractRoleSuffix(r).toLowerCase()),
  );
}
